import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Newspaper, Play } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { dateLocale } from "../content/i18n_extra";
import { JapanMascot } from "./JapaneseMotifs";

export function postLoc(post, lang, field) {
  const local = post[`${field}_${lang}`];
  if (local) return local;
  return post[`${field}_en`] || post[`${field}_tr`] || "";
}

export function isNew(createdAt) {
  if (!createdAt) return false;
  return Date.now() - new Date(createdAt).getTime() < 7 * 24 * 3600 * 1000;
}

export function NewsSection() {
  const { t, lang } = useLang();
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/posts").then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  if (posts.length === 0) return null;

  return (
    <section className="bg-ice py-28 sm:py-36" data-testid="news-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }} className="max-w-3xl">
          <div className="text-xs font-heading font-bold tracking-[0.18em] sm:tracking-[0.3em] uppercase text-brand mb-4 flex items-center gap-2"><Newspaper size={14} /> {t("sec_news_kicker")}</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-ink text-balance">{t("sec_news_title")}</h2>
          <p className="mt-5 text-lg text-sub leading-relaxed">{t("sec_news_sub")}</p>
        </motion.div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-5">
          {posts.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              data-testid={`news-card-${p.id}`}
            >
              <Link to={`/haberler/${p.id}`} className="group block bg-white rounded-3xl border border-black/5 overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:shadow-brand/10 transition-[transform,box-shadow] duration-300 h-full">
                {p.image_url ? (
                  <div className="relative h-44 overflow-hidden">
                    <img src={p.image_url} alt={postLoc(p, lang, "title")} loading="lazy" onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    {p.video_url && <span data-testid={`news-tudi-badge-${p.id}`} className="absolute top-3 left-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-ink/80 backdrop-blur text-white text-[10px] font-bold uppercase tracking-wider"><Play size={11} /> {t("news_tudi_badge")}</span>}
                  </div>
                ) : (
                  <div className="relative h-44 overflow-hidden bg-gradient-to-br from-ice via-mist to-blush flex items-center justify-center" data-testid={`news-mascot-tile-${p.id}`}>
                    <JapanMascot className="w-28 opacity-90 group-hover:scale-105 transition-transform duration-700" />
                  </div>
                )}
                <div className="p-7">
                  <div className="flex items-center gap-2 text-[10px] font-bold tracking-widest uppercase">
                    {p.tag && <span className="px-2.5 py-1 rounded-full bg-brand/10 text-brand">{p.tag}</span>}
                    {isNew(p.created_at) && <span data-testid="news-new-badge" className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 animate-pulse">{t("news_new")}</span>}
                    <span className="text-sub">{new Date(p.created_at).toLocaleDateString(dateLocale(lang))}</span>
                  </div>
                  <h3 className="mt-3.5 font-heading text-lg font-bold text-ink leading-snug">{postLoc(p, lang, "title")}</h3>
                  <p className="mt-2.5 text-sm text-ink/60 leading-relaxed line-clamp-3">{postLoc(p, lang, "excerpt")}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-bold text-brand group-hover:gap-2.5 transition-[gap] duration-200">
                    {t("news_read_more")} <ArrowRight size={13} />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="mt-10">
          <Link to="/haberler" data-testid="news-see-all" className="inline-flex items-center gap-2 text-sm font-semibold text-brand hover:gap-3 transition-[gap] duration-300">
            {t("news_all")} <ArrowRight size={15} />
          </Link>
        </div>
      </div>
    </section>
  );
}
